import { type RefObject, useEffect, useRef } from "react";

/**
 * Shared infinite-scroll: returns a ref to attach to a sentinel element at the
 * end of a list; when it scrolls into view (within `rootMargin`) and another
 * page isn't already loading, `fetchNextPage` fires. Pass `rootRef` when the
 * list scrolls inside a container (e.g. a board column) rather than the window.
 */
export function useInfiniteScroll<
  T extends HTMLElement = HTMLDivElement,
>(opts: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  rootRef?: RefObject<HTMLElement | null>;
  rootMargin?: string;
}): RefObject<T | null> {
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef,
    rootMargin = "300px",
  } = opts;
  const sentinelRef = useRef<T>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: rootRef?.current ?? null, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rootRef, rootMargin]);

  return sentinelRef;
}
